import React, { Suspense, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldErrors, UseFormMethods } from 'react-hook-form';
import SectionWrapper from 'pages/Layout/SectionWrapper';
import NavigationLink from 'components/links/NavigationLink';
import Input from 'components/inputs/Input';
import Button from 'components/buttons/Button';
import { SectionForm, InputWrapper, ButtonStyled } from '../../Styled';
import Spinner from 'components/Spinner/Spinner';
import { formatSelectOptions } from 'helpers/selectFormatHelper';
import { AdministratorData } from 'services/rest/SuperAdmin/Administrator/createAdministrator';
import { List as LoadingState } from 'react-content-loader';
import DropdownSearchable from 'components/inputs/DropdownSearchable';

export interface AgentFormProps {
  onSubmit: (event: React.FormEvent) => void;
  errors: FieldErrors<AdministratorData>;
  register: UseFormMethods['register'];
  agent: any;
  setValue: any;
  control: any;
  loader: boolean;
  companies: any;
  loading: any;
  selectedCompanies: any;
  setSelectedCompanies: any;
}

const UpdateAgentForm: React.FC<AgentFormProps> = props => {
  const {
    onSubmit,
    errors,
    register,
    agent,
    setValue,
    loader,
    companies,
    loading,
    selectedCompanies,
    setSelectedCompanies
  } = props;
  const { t } = useTranslation(['super_admin_agent']);
  const options = useMemo(() => {
    return (
      companies?.map((company: any) => ({
        value: company.id,
        label: company.name
      })) || []
    );
  }, [companies]);

  useEffect(() => {
    if (agent && agent.companies && options && options.length > 0) {
      const agentCompanyIds = agent.companies.map((company: any) => company.id);

      const preselectedCompanies = options.filter((option: any) =>
        agentCompanyIds.includes(option.value)
      );

      setSelectedCompanies(preselectedCompanies);
      setValue('companyIds', agentCompanyIds);
    }
  }, [agent, options, setSelectedCompanies, setValue]);

  if (loading) {
    return <LoadingState height="230px" width="100%" />;
  }
  return (
    <Suspense fallback={<LoadingState height="230px" width="100%" />}>
      <SectionWrapper>
        <NavigationLink
          text={t('super_admin_agent:form:back')}
          direction="back"
          to={'/agents'}
        />
        <h1>{t('super_admin_agent:edit_agent')}</h1>
        <form onSubmit={onSubmit}>
          <SectionForm>
            <InputWrapper>
              <label>{t('super_admin_agent:form:name')}</label>
              <div className="w-100">
                <Input
                  name="name"
                  type="text"
                  placeholder={t('super_admin_agent:form:name')}
                  defaultValue={agent?.name}
                  ref={register}
                  error={errors.name?.message}
                />
              </div>
            </InputWrapper>
            <InputWrapper>
              <label>{t('super_admin_agent:form:lastName')}</label>
              <div className="w-100">
                <Input
                  name="lastName"
                  type="text"
                  placeholder={t('super_admin_agent:form:lastName')}
                  defaultValue={agent?.lastName}
                  ref={register}
                  error={errors.name?.message}
                />
              </div>
            </InputWrapper>
            <InputWrapper>
              <label>{t('super_admin_agent:form:email')}</label>
              <div className="w-100">
                <Input
                  name="email"
                  type="text"
                  placeholder={t('super_admin_agent:form:email')}
                  defaultValue={agent?.email}
                  ref={register}
                  error={errors.email?.message}
                />
              </div>
            </InputWrapper>

            <InputWrapper>
              <label>{t('super_admin_agent:form:company')}</label>
              <div className="w-100">
                <DropdownSearchable
                  name="companyIds"
                  isSearchable
                  isMulti={true}
                  placeHolder="Seleccione compañías"
                  options={options}
                  initialValue={selectedCompanies}
                  onChange={selectedValue => {
                    let optionsArray: any[] = [];

                    if (selectedValue) {
                      optionsArray = Array.isArray(selectedValue)
                        ? selectedValue
                        : [selectedValue];
                    }

                    const companyIds = optionsArray.map(option => option.value);
                    setSelectedCompanies(optionsArray);
                    setValue('companyIds', companyIds);
                  }}
                />
              </div>
            </InputWrapper>
          </SectionForm>
          <ButtonStyled>
            {!loader ? (
              <Button value={t('super_admin_agent:update')} variant="primary" />
            ) : (
              <Spinner name="sign-up" />
            )}
          </ButtonStyled>
        </form>
      </SectionWrapper>
    </Suspense>
  );
};

export default UpdateAgentForm;
